import Phaser from 'phaser';
import {
  DEFAULT_COSMETICS,
  getCoreSkin,
  getSkillSkin,
  type CosmeticLoadout,
} from '../../data/cosmetics';
import { getCore, getEnemy } from '../../data/content';
import type { PlayerSettings } from '../../services/save';
import { Simulation } from '../simulation/Simulation';
import {
  CORE_X,
  CORE_Y,
  FIXED_STEP,
  WORLD_HEIGHT,
  WORLD_WIDTH,
  type EnemyState,
  type GameEvent,
  type ProjectileState,
  type SimulationCommand,
  type SimulationSnapshot,
  type WeaponFamily,
  type XpPickupState,
  type XpTier,
} from '../simulation/types';

export interface RuntimeFrame {
  snapshot: SimulationSnapshot;
  events: GameEvent[];
}

export class BattleScene extends Phaser.Scene {
  onFrame: ((frame: RuntimeFrame) => void) | null = null;

  private simulation: Simulation | null = null;
  private accumulator = 0;
  private speed = 1;
  private commands: SimulationCommand[] = [];
  private settings: PlayerSettings | null = null;
  private cosmetics: CosmeticLoadout = { ...DEFAULT_COSMETICS };
  private coreGraphics!: Phaser.GameObjects.Graphics;
  private barsGraphics!: Phaser.GameObjects.Graphics;
  private hudGraphics!: Phaser.GameObjects.Graphics;
  private arenaGraphics!: Phaser.GameObjects.Graphics;
  private environmentGraphics!: Phaser.GameObjects.Graphics;
  private enemyAuraGraphics!: Phaser.GameObjects.Graphics;
  private enemyDetailGraphics!: Phaser.GameObjects.Graphics;
  private projectileTrailGraphics!: Phaser.GameObjects.Graphics;
  private xpGlowGraphics!: Phaser.GameObjects.Graphics;
  private joystickGraphics!: Phaser.GameObjects.Graphics;
  private waveText!: Phaser.GameObjects.Text;
  private cashText!: Phaser.GameObjects.Text;
  private statsText!: Phaser.GameObjects.Text;
  private bossText!: Phaser.GameObjects.Text;
  private comboText!: Phaser.GameObjects.Text;
  private abilityText!: Phaser.GameObjects.Text;
  private background!: Phaser.GameObjects.TileSprite;
  private endgameBackground!: Phaser.GameObjects.TileSprite;
  private ascendantCoreSprite!: Phaser.GameObjects.Image;
  private readonly enemyViews = new Map<number, Phaser.GameObjects.Polygon>();
  private readonly projectileViews = new Map<number, Phaser.GameObjects.Arc>();
  private readonly xpPickupViews = new Map<number, Phaser.GameObjects.Polygon>();
  private readonly enemyViewPools = new Map<string, Phaser.GameObjects.Polygon[]>();
  private readonly projectileViewPools = new Map<'player' | 'enemy', Phaser.GameObjects.Arc[]>();
  private readonly xpPickupViewPools = new Map<XpTier, Phaser.GameObjects.Polygon[]>();
  private previousTimestamp = 0;
  private dynamicZoom = 1;
  private joystickPointerId: number | null = null;
  private joystickOrigin = new Phaser.Math.Vector2();
  private joystickVector = new Phaser.Math.Vector2();
  private moveKeys: Record<'up' | 'down' | 'left' | 'right', Phaser.Input.Keyboard.Key> | null =
    null;
  private cursorKeys: Phaser.Types.Input.Keyboard.CursorKeys | null = null;
  private lastKeyboardX = 0;
  private lastKeyboardY = 0;
  private currentSnapshot: SimulationSnapshot | null = null;
  private cameraPunch = 0;
  private endgameEntrancePlayed = false;
  private lastCelebratedComboTier = 0;

  constructor() {
    super('battle');
  }

  preload(): void {
    this.load.image('arena', 'assets/neocore-arena.png');
    this.load.image('arena-endgame', 'assets/neocore-endgame-arena.png');
    this.load.image('singularity-ascendant', 'assets/singularity-core-ascendant.png');
  }

  create(): void {
    this.background = this.add
      .tileSprite(WORLD_WIDTH / 2, WORLD_HEIGHT / 2, WORLD_WIDTH * 1.8, WORLD_HEIGHT * 1.8, 'arena')
      .setScrollFactor(0)
      .setDepth(-30)
      .setAlpha(0.72);
    this.endgameBackground = this.add
      .tileSprite(
        WORLD_WIDTH / 2,
        WORLD_HEIGHT / 2,
        WORLD_WIDTH * 1.9,
        WORLD_HEIGHT * 1.9,
        'arena-endgame',
      )
      .setScrollFactor(0)
      .setDepth(-29)
      .setAlpha(0);
    this.add
      .rectangle(
        WORLD_WIDTH / 2,
        WORLD_HEIGHT / 2,
        WORLD_WIDTH * 1.4,
        WORLD_HEIGHT * 1.4,
        0x05070f,
        0.2,
      )
      .setScrollFactor(0)
      .setDepth(-28);
    this.environmentGraphics = this.add
      .graphics()
      .setDepth(-20)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.arenaGraphics = this.add.graphics().setDepth(8);
    this.enemyAuraGraphics = this.add.graphics().setDepth(9).setBlendMode(Phaser.BlendModes.ADD);
    this.enemyDetailGraphics = this.add
      .graphics()
      .setDepth(10.6)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.projectileTrailGraphics = this.add
      .graphics()
      .setDepth(11)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.xpGlowGraphics = this.add.graphics().setDepth(10.8).setBlendMode(Phaser.BlendModes.ADD);
    this.coreGraphics = this.add.graphics().setDepth(15);
    this.ascendantCoreSprite = this.add
      .image(CORE_X, CORE_Y, 'singularity-ascendant')
      .setDepth(16)
      .setVisible(false);
    this.barsGraphics = this.add.graphics().setDepth(30);
    this.hudGraphics = this.add.graphics().setDepth(31).setScrollFactor(0);
    this.joystickGraphics = this.add.graphics().setDepth(80).setScrollFactor(0);

    const textStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: 'Oxanium, sans-serif',
      color: '#d9fbff',
      fontSize: '15px',
      fontStyle: 'bold',
      stroke: '#05070f',
      strokeThickness: 4,
    };
    this.cashText = this.add.text(18, 20, '$ 0', textStyle).setDepth(35).setScrollFactor(0);
    this.waveText = this.add
      .text(WORLD_WIDTH / 2, 20, 'WAVE 1', textStyle)
      .setOrigin(0.5, 0)
      .setDepth(35)
      .setScrollFactor(0);
    this.bossText = this.add
      .text(WORLD_WIDTH / 2, 55, '', { ...textStyle, color: '#ff4f7b', fontSize: '12px' })
      .setOrigin(0.5, 0)
      .setDepth(35)
      .setScrollFactor(0);
    this.comboText = this.add
      .text(WORLD_WIDTH - 18, 70, '', {
        ...textStyle,
        align: 'right',
        color: '#ffd321',
        fontSize: '12px',
      })
      .setOrigin(1, 0)
      .setDepth(35)
      .setScrollFactor(0);
    this.abilityText = this.add
      .text(18, 48, '', {
        ...textStyle,
        color: '#8cf5ff',
        fontSize: '9px',
      })
      .setDepth(35)
      .setScrollFactor(0);
    this.statsText = this.add
      .text(12, WORLD_HEIGHT - 70, '', {
        ...textStyle,
        fontFamily: 'Space Grotesk, sans-serif',
        fontSize: '10px',
        color: '#7fb5c1',
      })
      .setDepth(35)
      .setScrollFactor(0);
    if (this.input.keyboard) {
      this.moveKeys = this.input.keyboard.addKeys({
        up: Phaser.Input.Keyboard.KeyCodes.W,
        down: Phaser.Input.Keyboard.KeyCodes.S,
        left: Phaser.Input.Keyboard.KeyCodes.A,
        right: Phaser.Input.Keyboard.KeyCodes.D,
      }) as Record<'up' | 'down' | 'left' | 'right', Phaser.Input.Keyboard.Key>;
      this.cursorKeys = this.input.keyboard.createCursorKeys();
    }
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => this.startJoystick(pointer));
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => this.moveJoystick(pointer));
    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => this.stopJoystick(pointer));
    this.input.on('pointerupoutside', (pointer: Phaser.Input.Pointer) =>
      this.stopJoystick(pointer),
    );
    this.drawIdleCore();
  }

  override update(time: number, deltaMs: number): void {
    if (!this.simulation) return;
    this.updateKeyboardMovement();
    const delta = Math.min(0.1, deltaMs / 1000) * this.speed;
    this.accumulator += delta;
    let steps = 0;
    while (this.accumulator >= FIXED_STEP && steps < 12) {
      this.simulation.step(this.commands);
      this.commands = [];
      this.accumulator -= FIXED_STEP;
      steps += 1;
    }
    if (steps === 12) this.accumulator = 0;

    const snapshot = this.simulation.getSnapshot();
    const events = this.simulation.drainEvents();
    this.renderSnapshot(snapshot);
    this.renderEvents(events);
    this.onFrame?.({ snapshot, events });

    const elapsed =
      this.previousTimestamp === 0 ? 0 : Math.max(0.0001, (time - this.previousTimestamp) / 1000);
    this.previousTimestamp = time;
    if (import.meta.env.DEV) {
      this.statsText.setText(
        `FPS ${Math.round(1 / elapsed || 0)}  ENEMIES ${snapshot.enemies.length}  PROJECTILES ${snapshot.projectiles.length}  XP ${snapshot.xpPickups.length}`,
      );
    } else this.statsText.setText('');
  }

  setSimulation(simulation: Simulation | null): void {
    this.simulation = simulation;
    this.accumulator = 0;
    this.commands = [];
    this.dynamicZoom = 1;
    this.cameraPunch = 0;
    this.endgameEntrancePlayed = false;
    this.lastCelebratedComboTier = 0;
    this.currentSnapshot = simulation?.getSnapshot() ?? null;
    const position = this.currentSnapshot?.player ?? { x: CORE_X, y: CORE_Y };
    this.cameras.main.setZoom(1).centerOn(position.x, position.y);
    this.stopJoystick();
    if (!simulation) {
      this.clearEntityViews();
      this.drawIdleCore();
    }
  }

  enqueue(command: SimulationCommand): void {
    this.commands.push(command);
  }

  setSpeed(speed: number): void {
    this.speed = speed;
  }

  setSettings(settings: PlayerSettings): void {
    this.settings = settings;
    const qualityAlpha =
      settings.graphicsQuality === 'low'
        ? 0.5
        : settings.graphicsQuality === 'medium'
          ? 0.62
          : 0.72;
    this.background?.setAlpha(qualityAlpha);
    this.endgameBackground?.setAlpha(0);
  }

  setCosmetics(cosmetics: CosmeticLoadout): void {
    this.cosmetics = { ...cosmetics };
    if (!this.simulation) this.drawIdleCore();
  }

  private renderSnapshot(snapshot: SimulationSnapshot): void {
    this.currentSnapshot = snapshot;
    this.updateCamera(snapshot);
    this.renderInfiniteBackground(snapshot);
    this.renderBossArena(snapshot);
    this.waveText.setText(`WAVE ${snapshot.wave}/10`);
    this.cashText.setText(`$ ${snapshot.cash.toLocaleString('en-US')}`);
    const boss = snapshot.enemies.find(
      (enemy) => enemy.kind === 'warden' || enemy.kind === 'devourer',
    );
    const huntProtocol =
      snapshot.waveSpawnFinished &&
      snapshot.enemies.length > 0 &&
      snapshot.enemies.length <= 5 &&
      !boss;
    const nearestHuntDistance = huntProtocol
      ? Math.min(
          ...snapshot.enemies.map((enemy) =>
            Math.hypot(enemy.x - snapshot.player.x, enemy.y - snapshot.player.y),
          ),
        )
      : 0;
    this.bossText.setText(
      boss
        ? `⚠ ${getEnemy(boss.kind).name.toUpperCase()}  ·  PHASE ${boss.bossPhase}`
        : huntProtocol
          ? `⌖ HUNT PROTOCOL · ${snapshot.enemies.length} TARGET${snapshot.enemies.length === 1 ? '' : 'S'} · ${Math.round(nearestHuntDistance)}m`
          : '',
    );
    this.comboText.setText(
      snapshot.combo >= 2
        ? `×${snapshot.combo} COMBO\n${snapshot.overdriveFor > 0 ? 'OVERDRIVE' : `${Math.round(snapshot.momentum)}% MOMENTUM`}`
        : snapshot.overdriveFor > 0
          ? 'OVERDRIVE'
          : '',
    );
    const coreDefinition = getCore(snapshot.coreId);
    this.abilityText.setText(
      `${coreDefinition.abilityName.toUpperCase()} · ${Math.max(0, snapshot.coreAbilityCooldown).toFixed(1)}s`,
    );
    this.renderCore(snapshot);
    this.renderEnemies(snapshot.enemies);
    this.renderProjectiles(snapshot.projectiles);
    this.renderXpPickups(snapshot.xpPickups, snapshot);
    this.renderBars(snapshot, boss);
    if (this.isEndgameSnapshot(snapshot) && !this.endgameEntrancePlayed) {
      this.endgameEntrancePlayed = true;
      this.playEndgameEntrance(snapshot);
    }
  }

  private updateCamera(snapshot: SimulationSnapshot): void {
    const levelPressure = Math.max(0, snapshot.level - 4) * 0.012;
    const densityPressure = Math.max(0, snapshot.enemies.length - 24) * 0.0018;
    const bossPressure = snapshot.enemies.some(
      (enemy) => enemy.kind === 'warden' || enemy.kind === 'devourer',
    )
      ? 0.045
      : 0;
    const target = Phaser.Math.Clamp(
      1 - Math.min(0.13, levelPressure) - Math.min(0.14, densityPressure) - bossPressure,
      0.68,
      1,
    );
    this.dynamicZoom = Phaser.Math.Linear(this.dynamicZoom, target, 0.045);
    this.cameraPunch *= 0.86;
    this.cameras.main
      .setZoom(this.dynamicZoom + this.cameraPunch)
      .centerOn(snapshot.player.x, snapshot.player.y);
  }

  private renderInfiniteBackground(snapshot: SimulationSnapshot): void {
    this.background.tilePositionX = snapshot.player.x * 0.42;
    this.background.tilePositionY = snapshot.player.y * 0.42;
    this.endgameBackground.tilePositionX = snapshot.player.x * 0.18 - snapshot.tick * 0.035;
    this.endgameBackground.tilePositionY = snapshot.player.y * 0.18 + snapshot.tick * 0.022;
    const intensity = this.visualIntensity(snapshot);
    const quality = this.qualityFactor();
    const endgame = this.isEndgameSnapshot(snapshot);
    this.background.setAlpha(quality * (endgame ? 0.18 : 0.72 - intensity * 0.2));
    this.endgameBackground.setAlpha(quality * (endgame ? 0.82 : intensity * 0.22));
    this.background.setTint(
      Phaser.Display.Color.GetColor(
        Math.round(255 - intensity * 25),
        Math.round(255 - intensity * 8),
        255,
      ),
    );
    this.endgameBackground.setTint(
      Phaser.Display.Color.GetColor(230 + Math.round(intensity * 25), 220, 255),
    );
    this.renderEnvironmentEnergy(snapshot, intensity);
  }

  private renderEnvironmentEnergy(snapshot: SimulationSnapshot, intensity: number): void {
    this.environmentGraphics.clear();
    if (this.settings?.graphicsQuality === 'low') return;
    const x = snapshot.player.x;
    const y = snapshot.player.y;
    const time = snapshot.tick * 0.012;
    const endgameBoost = this.isEndgameSnapshot(snapshot) ? 1 : 0;
    const alpha = 0.045 + intensity * 0.085;
    for (let ring = 0; ring < 4; ring += 1) {
      const radius = 105 + ring * 78 + Math.sin(time * (1.2 + ring * 0.15)) * 12;
      const color = ring % 2 === 0 ? 0xc33cff : endgameBoost ? 0xffd321 : 0x00e5ff;
      this.environmentGraphics.lineStyle(1 + endgameBoost, color, alpha);
      this.environmentGraphics.strokeCircle(x, y, radius);
      const segments = 8 + ring * 2;
      for (let index = 0; index < segments; index += 1) {
        const angle = (Math.PI * 2 * index) / segments + time * (ring % 2 === 0 ? 0.32 : -0.22);
        const length = index % 3 === 0 ? 16 : 7;
        this.environmentGraphics.lineBetween(
          x + Math.cos(angle) * (radius - length),
          y + Math.sin(angle) * (radius - length),
          x + Math.cos(angle) * (radius + length),
          y + Math.sin(angle) * (radius + length),
        );
      }
    }
    const motes = 12 + Math.round(intensity * 18);
    for (let index = 0; index < motes; index += 1) {
      const seed = index * 91.73;
      const angle = seed + time * (0.16 + (index % 4) * 0.03);
      const radius = 90 + ((index * 53) % 330);
      const color = index % 3 === 0 ? 0xffd321 : index % 2 === 0 ? 0xc33cff : 0x00e5ff;
      this.environmentGraphics
        .fillStyle(color, alpha * 2.2)
        .fillCircle(x + Math.cos(angle) * radius, y + Math.sin(angle) * radius, 1.2 + (index % 3));
    }
  }

  private renderBossArena(snapshot: SimulationSnapshot): void {
    this.arenaGraphics.clear();
    const arena = snapshot.bossArena;
    if (!arena.active) return;
    const color = arena.kind ? getEnemy(arena.kind).color : 0xc33cff;
    const pulse = 0.72 + Math.sin(arena.pulse * 4.5) * 0.16;
    this.arenaGraphics
      .fillStyle(0x120818, 0.12)
      .fillCircle(arena.centerX, arena.centerY, arena.radius);
    this.arenaGraphics
      .lineStyle(10, color, 0.08)
      .strokeCircle(arena.centerX, arena.centerY, arena.radius - 4);
    this.arenaGraphics
      .lineStyle(3, color, pulse)
      .strokeCircle(arena.centerX, arena.centerY, arena.radius);
    for (let index = 0; index < 24; index += 1) {
      const angle = (Math.PI * 2 * index) / 24 + arena.pulse * 0.35;
      const inner = arena.radius - 14;
      const outer = arena.radius + 5;
      this.arenaGraphics.lineStyle(index % 3 === 0 ? 3 : 1, color, index % 3 === 0 ? 0.9 : 0.42);
      this.arenaGraphics.lineBetween(
        arena.centerX + Math.cos(angle) * inner,
        arena.centerY + Math.sin(angle) * inner,
        arena.centerX + Math.cos(angle) * outer,
        arena.centerY + Math.sin(angle) * outer,
      );
    }
  }

  private renderXpPickups(pickups: readonly XpPickupState[], snapshot: SimulationSnapshot): void {
    this.xpGlowGraphics.clear();
    const activeIds = new Set(pickups.map((pickup) => pickup.id));
    for (const [id, view] of this.xpPickupViews) {
      if (activeIds.has(id)) continue;
      view.setVisible(false).setActive(false);
      const tier = view.getData('tier') as XpTier;
      const pool = this.xpPickupViewPools.get(tier) ?? [];
      pool.push(view);
      this.xpPickupViewPools.set(tier, pool);
      this.xpPickupViews.delete(id);
    }

    for (const pickup of pickups) {
      let view = this.xpPickupViews.get(pickup.id);
      if (!view) {
        const pool = this.xpPickupViewPools.get(pickup.tier) ?? [];
        view = pool.pop();
        if (!view) {
          view = this.add.polygon(
            pickup.x,
            pickup.y,
            this.regularPolygon(4, pickup.radius),
            this.xpColor(pickup.tier),
            0.96,
          );
        }
        view
          .setVisible(true)
          .setActive(true)
          .setData('tier', pickup.tier)
          .setStrokeStyle(
            pickup.tier === 'gray' ? 1 : 2,
            pickup.tier === 'gray' ? 0xe3ebef : 0xffffff,
            0.9,
          )
          .setDepth(11);
        this.xpPickupViews.set(pickup.id, view);
      }
      const color = this.xpColor(pickup.tier);
      const tierPower = pickup.tier === 'prismatic' ? 0.18 : pickup.tier === 'gold' ? 0.12 : 0.07;
      const progressionPulse = Math.min(0.08, snapshot.level * 0.004);
      const pulse =
        1 + Math.sin(pickup.age * (4.5 + (pickup.id % 3))) * (tierPower + progressionPulse);
      this.xpGlowGraphics
        .fillStyle(color, pickup.tier === 'prismatic' ? 0.16 : pickup.tier === 'gold' ? 0.12 : 0.06)
        .fillCircle(pickup.x, pickup.y, pickup.radius * (pickup.tier === 'prismatic' ? 3.2 : 2.4));
      if (pickup.tier === 'gold' || pickup.tier === 'prismatic') {
        const flare = pickup.radius * (2.4 + Math.sin(pickup.age * 5) * 0.3);
        this.xpGlowGraphics.lineStyle(1, color, 0.65);
        this.xpGlowGraphics.lineBetween(pickup.x - flare, pickup.y, pickup.x + flare, pickup.y);
        this.xpGlowGraphics.lineBetween(pickup.x, pickup.y - flare, pickup.x, pickup.y + flare);
      }
      if (pickup.magnetized && this.settings?.graphicsQuality !== 'low') {
        this.xpGlowGraphics
          .lineStyle(1.5, color, 0.18)
          .lineBetween(pickup.x, pickup.y, snapshot.player.x, snapshot.player.y);
      }
      view
        .setPosition(
          Phaser.Math.Linear(view.x, pickup.x, pickup.magnetized ? 0.72 : 0.5),
          Phaser.Math.Linear(view.y, pickup.y, pickup.magnetized ? 0.72 : 0.5),
        )
        .setRotation(Math.PI / 4 + pickup.age * (pickup.magnetized ? 4.4 : 1.15))
        .setScale(pulse * (pickup.magnetized ? 1.12 : 1))
        .setFillStyle(color, pickup.magnetized ? 1 : 0.94)
        .setData('tier', pickup.tier);
    }
  }

  private xpColor(tier: XpTier): number {
    if (tier === 'gray') return 0x9ca9b3;
    if (tier === 'blue') return 0x32b8ff;
    if (tier === 'gold') return 0xffc82e;
    return 0xd45cff;
  }

  private startJoystick(pointer: Phaser.Input.Pointer): void {
    if (this.joystickPointerId !== null || this.currentSnapshot?.phase !== 'combat') return;
    this.joystickPointerId = pointer.id;
    this.joystickOrigin.set(pointer.x, pointer.y);
    this.joystickVector.set(0, 0);
    this.drawJoystick();
  }

  private moveJoystick(pointer: Phaser.Input.Pointer): void {
    if (pointer.id !== this.joystickPointerId || !pointer.isDown) return;
    const maxDistance = 58;
    this.joystickVector.set(pointer.x - this.joystickOrigin.x, pointer.y - this.joystickOrigin.y);
    if (this.joystickVector.length() > maxDistance) this.joystickVector.setLength(maxDistance);
    this.drawJoystick();
    this.enqueue({
      type: 'set-movement',
      x: this.joystickVector.x / maxDistance,
      y: this.joystickVector.y / maxDistance,
    });
  }

  private stopJoystick(pointer?: Phaser.Input.Pointer): void {
    if (pointer && pointer.id !== this.joystickPointerId) return;
    if (this.joystickPointerId !== null) this.enqueue({ type: 'set-movement', x: 0, y: 0 });
    this.joystickPointerId = null;
    this.joystickVector.set(0, 0);
    this.joystickGraphics?.clear();
  }

  private drawJoystick(): void {
    this.joystickGraphics.clear();
    const x = this.joystickOrigin.x;
    const y = this.joystickOrigin.y;
    this.joystickGraphics.fillStyle(0x05131d, 0.48).fillCircle(x, y, 58);
    this.joystickGraphics.lineStyle(2, 0x00e5ff, 0.45).strokeCircle(x, y, 58);
    this.joystickGraphics.lineStyle(1, 0x80ff24, 0.3).strokeCircle(x, y, 34);
    this.joystickGraphics
      .fillStyle(0x00e5ff, 0.78)
      .fillCircle(x + this.joystickVector.x, y + this.joystickVector.y, 20);
    this.joystickGraphics
      .lineStyle(2, 0xdfffff, 0.8)
      .strokeCircle(x + this.joystickVector.x, y + this.joystickVector.y, 20);
  }

  private updateKeyboardMovement(): void {
    if (!this.moveKeys || this.joystickPointerId !== null) return;
    const left = this.moveKeys.left.isDown || this.cursorKeys?.left.isDown;
    const right = this.moveKeys.right.isDown || this.cursorKeys?.right.isDown;
    const up = this.moveKeys.up.isDown || this.cursorKeys?.up.isDown;
    const down = this.moveKeys.down.isDown || this.cursorKeys?.down.isDown;
    let x = Number(Boolean(right)) - Number(Boolean(left));
    let y = Number(Boolean(down)) - Number(Boolean(up));
    const length = Math.hypot(x, y);
    if (length > 1) {
      x /= length;
      y /= length;
    }
    if (x === this.lastKeyboardX && y === this.lastKeyboardY) return;
    this.lastKeyboardX = x;
    this.lastKeyboardY = y;
    this.enqueue({ type: 'set-movement', x, y });
  }

  private renderCore(snapshot: SimulationSnapshot): void {
    const evolution = snapshot.evolutionId;
    const enabledWeapons = snapshot.weapons.filter((weapon) => weapon.enabled).length;
    const coreX = snapshot.player.x;
    const coreY = snapshot.player.y;
    const ascension = Math.min(5, Math.floor((snapshot.level - 1) / 3));
    const intensity = this.visualIntensity(snapshot);
    const endgame = this.isEndgameSnapshot(snapshot);
    const skin = getCoreSkin(this.cosmetics.coreSkinId);
    if (endgame) {
      const spritePulse = 98 + Math.sin(snapshot.tick * 0.09) * 5;
      this.ascendantCoreSprite
        .setVisible(true)
        .setPosition(coreX, coreY)
        .setDisplaySize(spritePulse, spritePulse)
        .setRotation(snapshot.tick * 0.0022)
        .setAlpha(0.98)
        .setTint(skin.glow);
    } else {
      this.ascendantCoreSprite.setVisible(false);
    }
    this.coreGraphics.clear();
    for (let glow = 4; glow >= 1; glow -= 1) {
      this.coreGraphics
        .fillStyle(glow % 2 === 0 ? skin.primary : skin.secondary, 0.012 + intensity * 0.012)
        .fillCircle(coreX, coreY, 46 + glow * 16 + intensity * 22);
    }
    const rayCount = 8 + Math.round(intensity * 8);
    for (let index = 0; index < rayCount; index += 1) {
      const angle =
        (Math.PI * 2 * index) / rayCount + snapshot.tick * (index % 2 === 0 ? 0.009 : -0.006);
      const inner = 38 + Math.sin(snapshot.tick * 0.08 + index) * 3;
      const outer = 54 + intensity * 34 + (index % 3) * 6;
      this.coreGraphics.lineStyle(
        index % 4 === 0 ? 2 : 1,
        index % 2 ? skin.secondary : skin.primary,
        0.18 + intensity * 0.22,
      );
      this.coreGraphics.lineBetween(
        coreX + Math.cos(angle) * inner,
        coreY + Math.sin(angle) * inner,
        coreX + Math.cos(angle) * outer,
        coreY + Math.sin(angle) * outer,
      );
    }
    this.coreGraphics.fillStyle(0x061827, 0.96).lineStyle(2.5, skin.primary, 0.98);
    this.coreGraphics
      .fillPoints(this.hexagon(coreX, coreY, 28 + ascension), true)
      .strokePoints(this.hexagon(coreX, coreY, 28 + ascension), true);
    this.coreGraphics.lineStyle(2, skin.secondary, 0.72);
    this.coreGraphics.strokeCircle(coreX, coreY, 34 + enabledWeapons * 4 + ascension * 2);
    if (snapshot.synergies.length > 0) {
      this.coreGraphics.lineStyle(1.5, skin.glow, 0.75);
      this.coreGraphics.strokeCircle(coreX, coreY, 46 + snapshot.synergies.length * 2);
    }
    if (snapshot.core.maxBarrier > 0 && snapshot.core.barrier > 0) {
      const ratio = snapshot.core.barrier / snapshot.core.maxBarrier;
      this.coreGraphics.lineStyle(4, skin.primary, 0.2 + ratio * 0.75);
      this.coreGraphics.beginPath();
      this.coreGraphics.arc(coreX, coreY, 42, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * ratio);
      this.coreGraphics.strokePath();
    }
    if (snapshot.overdriveFor > 0) {
      const pulse = 52 + Math.sin(snapshot.tick * 0.18) * 4;
      this.coreGraphics.lineStyle(endgame ? 5 : 3, 0xffd321, 0.75);
      this.coreGraphics.strokeCircle(coreX, coreY, pulse);
      this.coreGraphics.lineStyle(10, 0xffd321, 0.06).strokeCircle(coreX, coreY, pulse + 7);
    }
    if (evolution) {
      this.coreGraphics.lineStyle(3, skin.secondary, 0.9);
      this.coreGraphics.strokeCircle(coreX, coreY, 58);
      for (let index = 0; index < 6; index += 1) {
        const angle = (Math.PI * 2 * index) / 6 + snapshot.tick * 0.014;
        this.coreGraphics.fillStyle(index % 2 ? skin.primary : skin.secondary, 0.9);
        this.coreGraphics.fillCircle(coreX + Math.cos(angle) * 58, coreY + Math.sin(angle) * 58, 3);
      }
    }
    for (let index = 0; index < ascension; index += 1) {
      const orbit = 48 + index * 6;
      const angle =
        snapshot.tick * (0.012 + index * 0.002) + (Math.PI * 2 * index) / Math.max(1, ascension);
      this.coreGraphics.fillStyle(index % 2 === 0 ? skin.primary : skin.secondary, 0.9);
      this.coreGraphics.fillCircle(
        coreX + Math.cos(angle) * orbit,
        coreY + Math.sin(angle) * orbit,
        2.5 + index * 0.25,
      );
    }
    if (endgame) {
      for (let index = 0; index < 12; index += 1) {
        const angle = (Math.PI * 2 * index) / 12 - snapshot.tick * 0.021;
        const orbit = 76 + Math.sin(snapshot.tick * 0.055 + index) * 5;
        const color = index % 3 === 0 ? skin.glow : index % 2 === 0 ? skin.secondary : skin.primary;
        this.coreGraphics
          .fillStyle(color, 0.95)
          .fillCircle(
            coreX + Math.cos(angle) * orbit,
            coreY + Math.sin(angle) * orbit,
            index % 3 === 0 ? 4 : 2.5,
          );
      }
      this.coreGraphics
        .lineStyle(2, skin.glow, 0.34 + Math.sin(snapshot.tick * 0.11) * 0.12)
        .strokeCircle(coreX, coreY, 88);
    }
    if (Math.hypot(snapshot.player.velocityX, snapshot.player.velocityY) > 20) {
      const speed = Math.hypot(snapshot.player.velocityX, snapshot.player.velocityY) || 1;
      this.coreGraphics.lineStyle(3, skin.primary, 0.38);
      this.coreGraphics.lineBetween(
        coreX,
        coreY,
        coreX - (snapshot.player.velocityX / speed) * (18 + ascension * 3),
        coreY - (snapshot.player.velocityY / speed) * (18 + ascension * 3),
      );
    }
    this.coreGraphics.fillStyle(0xeaffff, 0.95).fillCircle(coreX, coreY, 6);
  }

  private drawIdleCore(): void {
    if (!this.coreGraphics) return;
    this.ascendantCoreSprite?.setVisible(false);
    this.coreGraphics.clear();
    const skin = getCoreSkin(this.cosmetics.coreSkinId);
    this.coreGraphics.fillStyle(0x061827, 0.96).lineStyle(2, skin.primary, 0.9);
    this.coreGraphics
      .fillPoints(this.hexagon(CORE_X, CORE_Y, 30), true)
      .strokePoints(this.hexagon(CORE_X, CORE_Y, 30), true);
    this.coreGraphics.lineStyle(1.5, skin.secondary, 0.5).strokeCircle(CORE_X, CORE_Y, 42);
  }

  private renderEnemies(enemies: readonly EnemyState[]): void {
    this.renderEnemyAuras(enemies);
    const activeIds = new Set(enemies.map((enemy) => enemy.id));
    for (const [id, view] of this.enemyViews) {
      if (!activeIds.has(id)) {
        view.setVisible(false).setActive(false);
        const kind = String(view.getData('kind'));
        const pool = this.enemyViewPools.get(kind) ?? [];
        pool.push(view);
        this.enemyViewPools.set(kind, pool);
        this.enemyViews.delete(id);
      }
    }

    for (const enemy of enemies) {
      let view = this.enemyViews.get(enemy.id);
      if (!view) {
        const definition = getEnemy(enemy.kind);
        const pool = this.enemyViewPools.get(enemy.kind) ?? [];
        view = pool.pop();
        if (!view) {
          view = this.add.polygon(
            0,
            0,
            this.regularPolygon(definition.sides, enemy.radius),
            definition.color,
            0.84,
          );
          view.setData('kind', enemy.kind);
        }
        view
          .setVisible(true)
          .setActive(true)
          .setStrokeStyle(
            enemy.eliteModifier ? 3 : 1.5,
            enemy.eliteModifier ? 0xffd321 : 0xffd9e2,
            0.9,
          )
          .setDepth(10);
        this.enemyViews.set(enemy.id, view);
      }
      const definition = getEnemy(enemy.kind);
      const frozen = enemy.status.frozenFor > 0;
      view.setPosition(
        Phaser.Math.Linear(view.x, enemy.x, 0.46),
        Phaser.Math.Linear(view.y, enemy.y, 0.46),
      );
      view.setRotation(
        enemy.kind === 'fast' ? Math.PI / 4 : enemy.id * 0.37 + enemy.health * 0.0002,
      );
      view.setFillStyle(
        enemy.hitFlash > 0.55 ? 0xffffff : frozen ? 0x63efff : definition.color,
        frozen ? 0.95 : 0.84,
      );
      const bossScale = enemy.kind === 'warden' || enemy.kind === 'devourer' ? 1.14 : 1;
      const livingPulse = 1 + Math.sin((this.currentSnapshot?.tick ?? 0) * 0.08 + enemy.id) * 0.035;
      view.setScale((enemy.bossShielded ? 1.1 : 1) * bossScale * livingPulse);
      view.setStrokeStyle(
        enemy.eliteModifier ? 3 : 1.5,
        enemy.eliteModifier ? 0xffd321 : 0xffd9e2,
        0.9,
      );
      if (enemy.kind === 'protector' || enemy.shield > 0) {
        view.setStrokeStyle(2.5, 0xc33cff, 1);
      }
      const huntProtocol =
        this.currentSnapshot?.waveSpawnFinished === true &&
        (this.currentSnapshot?.enemies.length ?? 0) > 0 &&
        (this.currentSnapshot?.enemies.length ?? 0) <= 5 &&
        !this.currentSnapshot?.enemies.some(
          (candidate) => candidate.kind === 'warden' || candidate.kind === 'devourer',
        );
      if (huntProtocol) {
        const pulse = 0.78 + Math.sin((this.currentSnapshot?.tick ?? 0) * 0.16 + enemy.id) * 0.2;
        view.setStrokeStyle(3, 0xffd321, pulse);
      }
    }
  }

  private renderEnemyAuras(enemies: readonly EnemyState[]): void {
    this.enemyAuraGraphics.clear();
    this.enemyDetailGraphics.clear();
    const lowQuality = this.settings?.graphicsQuality === 'low';
    const tick = this.currentSnapshot?.tick ?? 0;
    for (const enemy of enemies) {
      const definition = getEnemy(enemy.kind);
      const boss = enemy.kind === 'warden' || enemy.kind === 'devourer';
      const special = boss || enemy.eliteModifier || enemy.shield > 0 || enemy.status.frozenFor > 0;
      if (lowQuality && !special) continue;
      const color = enemy.status.frozenFor > 0 ? 0x63efff : definition.color;
      const pulse = 1 + Math.sin(tick * 0.09 + enemy.id) * 0.08;
      this.enemyAuraGraphics
        .fillStyle(0x000000, 0.3)
        .fillEllipse(
          enemy.x,
          enemy.y + enemy.radius * 0.72,
          enemy.radius * 2.5,
          enemy.radius * 1.05,
        );
      this.enemyAuraGraphics
        .fillStyle(color, boss ? 0.16 : special ? 0.1 : 0.045)
        .fillCircle(enemy.x, enemy.y, enemy.radius * (boss ? 2.3 : 1.75) * pulse);
      if (special) {
        this.enemyAuraGraphics
          .lineStyle(boss ? 3 : 1.5, color, boss ? 0.62 : 0.35)
          .strokeCircle(enemy.x, enemy.y, enemy.radius * (boss ? 1.75 : 1.42) * pulse);
      }
      if (enemy.shield > 0 || enemy.bossShielded) {
        const shieldRadius = enemy.radius * 1.7 + Math.sin(tick * 0.14 + enemy.id) * 3;
        this.enemyAuraGraphics
          .lineStyle(5, 0xc33cff, 0.1)
          .strokeCircle(enemy.x, enemy.y, shieldRadius);
        this.enemyAuraGraphics
          .lineStyle(1.5, 0xefd8ff, 0.7)
          .strokeCircle(enemy.x, enemy.y, shieldRadius);
      }
      this.renderEnemyDetails(enemy, color, tick);
    }
  }

  private renderEnemyDetails(enemy: EnemyState, color: number, tick: number): void {
    const x = enemy.x;
    const y = enemy.y;
    const radius = enemy.radius;
    this.enemyDetailGraphics
      .fillStyle(0xeaffff, 0.82)
      .fillCircle(x, y, Math.max(1.5, radius * 0.16));
    if (enemy.kind === 'fast') {
      const player = this.currentSnapshot?.player ?? { x: CORE_X, y: CORE_Y };
      const angle = Math.atan2(player.y - y, player.x - x);
      for (let trail = 0; trail < 2; trail += 1) {
        const distance = radius + 5 + trail * 6;
        const tailX = x - Math.cos(angle) * distance;
        const tailY = y - Math.sin(angle) * distance;
        this.enemyDetailGraphics.lineStyle(2, 0xffd321, 0.65 - trail * 0.2);
        this.enemyDetailGraphics.lineBetween(
          tailX + Math.cos(angle + 2.5) * 5,
          tailY + Math.sin(angle + 2.5) * 5,
          tailX,
          tailY,
        );
        this.enemyDetailGraphics.lineBetween(
          tailX + Math.cos(angle - 2.5) * 5,
          tailY + Math.sin(angle - 2.5) * 5,
          tailX,
          tailY,
        );
      }
    }
    if (enemy.kind === 'tank') {
      this.enemyDetailGraphics.lineStyle(2.4, 0xffd9a8, 0.7);
      for (let side = 0; side < 4; side += 1) {
        const angle = Math.PI / 4 + (Math.PI * 2 * side) / 4;
        this.enemyDetailGraphics.lineBetween(
          x + Math.cos(angle) * radius * 0.55,
          y + Math.sin(angle) * radius * 0.55,
          x + Math.cos(angle) * radius * 1.15,
          y + Math.sin(angle) * radius * 1.15,
        );
      }
    }
    if (enemy.kind === 'ranged') {
      const player = this.currentSnapshot?.player ?? { x: CORE_X, y: CORE_Y };
      const angle = Math.atan2(player.y - y, player.x - x);
      this.enemyDetailGraphics.lineStyle(2, 0xeaffff, 0.82);
      this.enemyDetailGraphics.lineBetween(
        x + Math.cos(angle) * 3,
        y + Math.sin(angle) * 3,
        x + Math.cos(angle) * (radius + 7),
        y + Math.sin(angle) * (radius + 7),
      );
      this.enemyDetailGraphics.lineStyle(1, 0x00e5ff, 0.5).strokeCircle(x, y, radius * 0.58);
    }
    if (enemy.kind === 'protector') {
      const pulse = radius * (0.68 + Math.sin(tick * 0.08 + enemy.id) * 0.06);
      this.enemyDetailGraphics
        .lineStyle(2, 0xf4dcff, 0.78)
        .strokeTriangle(
          x,
          y - pulse,
          x + pulse * 0.86,
          y + pulse * 0.5,
          x - pulse * 0.86,
          y + pulse * 0.5,
        );
    }
    if (enemy.kind === 'warden' || enemy.kind === 'devourer') {
      const spokes = enemy.kind === 'devourer' ? 12 : 8;
      for (let index = 0; index < spokes; index += 1) {
        const angle = (Math.PI * 2 * index) / spokes + tick * 0.012;
        this.enemyDetailGraphics.lineStyle(index % 3 === 0 ? 3 : 1, color, 0.7);
        this.enemyDetailGraphics.lineBetween(
          x + Math.cos(angle) * radius * 0.52,
          y + Math.sin(angle) * radius * 0.52,
          x + Math.cos(angle) * radius * 1.38,
          y + Math.sin(angle) * radius * 1.38,
        );
      }
      this.enemyDetailGraphics
        .lineStyle(2, 0xffffff, 0.62)
        .strokeCircle(x, y, radius * (0.72 + Math.sin(tick * 0.1) * 0.05));
    }
  }

  private renderProjectiles(projectiles: readonly ProjectileState[]): void {
    this.projectileTrailGraphics.clear();
    const activeIds = new Set(projectiles.map((projectile) => projectile.id));
    for (const [id, view] of this.projectileViews) {
      if (!activeIds.has(id)) {
        view.setVisible(false).setActive(false);
        const source = view.getData('source') as 'player' | 'enemy';
        const pool = this.projectileViewPools.get(source) ?? [];
        pool.push(view);
        this.projectileViewPools.set(source, pool);
        this.projectileViews.delete(id);
      }
    }
    for (const projectile of projectiles) {
      const color =
        projectile.source === 'player'
          ? getSkillSkin(this.cosmetics.skillSkinId).kinetic
          : 0xff265e;
      const deltaX = projectile.x - projectile.previousX;
      const deltaY = projectile.y - projectile.previousY;
      const trailMultiplier = projectile.source === 'player' ? 3.6 : 2.2;
      const tailX = projectile.x - deltaX * trailMultiplier;
      const tailY = projectile.y - deltaY * trailMultiplier;
      if (this.settings?.graphicsQuality !== 'low') {
        this.projectileTrailGraphics
          .lineStyle(projectile.source === 'player' ? 7 : 5, color, 0.1)
          .lineBetween(tailX, tailY, projectile.x, projectile.y);
      }
      this.projectileTrailGraphics
        .lineStyle(
          projectile.source === 'player' ? 2.2 : 1.5,
          projectile.source === 'player' ? 0xeaffff : color,
          0.82,
        )
        .lineBetween(tailX, tailY, projectile.x, projectile.y);
      let view = this.projectileViews.get(projectile.id);
      if (!view) {
        const pool = this.projectileViewPools.get(projectile.source) ?? [];
        view = pool.pop();
        if (!view) {
          view = this.add.circle(projectile.x, projectile.y, projectile.radius, color, 1);
          view.setData('source', projectile.source);
        }
        view
          .setVisible(true)
          .setActive(true)
          .setRadius(projectile.radius)
          .setFillStyle(color, 1)
          .setDepth(12);
        this.projectileViews.set(projectile.id, view);
      }
      view.setPosition(
        Phaser.Math.Linear(view.x, projectile.x, 0.68),
        Phaser.Math.Linear(view.y, projectile.y, 0.68),
      );
      const progressionScale =
        projectile.source === 'player'
          ? 1 + Math.min(0.65, ((this.currentSnapshot?.level ?? 1) - 1) * 0.035)
          : 1;
      view.setScale(progressionScale);
      if (projectile.source === 'player' && progressionScale > 1.35)
        view.setStrokeStyle(2, 0xffd321, 0.9);
    }
  }

  private renderBars(snapshot: SimulationSnapshot, boss: EnemyState | undefined): void {
    this.barsGraphics.clear();
    this.hudGraphics.clear();
    const hpRatio = snapshot.core.health / snapshot.core.maxHealth;
    const xpRatio = snapshot.xp / snapshot.xpToNext;
    this.drawBar(
      18,
      WORLD_HEIGHT - 42,
      WORLD_WIDTH - 36,
      11,
      hpRatio,
      0xff265e,
      0x28101c,
      this.hudGraphics,
    );
    this.drawBar(
      18,
      WORLD_HEIGHT - 24,
      WORLD_WIDTH - 36,
      6,
      xpRatio,
      0x00e5ff,
      0x071d26,
      this.hudGraphics,
    );
    this.drawBar(
      18,
      WORLD_HEIGHT - 14,
      WORLD_WIDTH - 36,
      3,
      snapshot.overdriveFor > 0 ? snapshot.overdriveFor / 6 : snapshot.momentum / 100,
      snapshot.overdriveFor > 0 ? 0xffd321 : 0x80ff24,
      0x0b2110,
      this.hudGraphics,
    );
    for (const enemy of snapshot.enemies) {
      if (
        enemy.kind !== 'tank' &&
        enemy.kind !== 'protector' &&
        enemy.kind !== 'warden' &&
        enemy.kind !== 'devourer' &&
        !enemy.eliteModifier
      )
        continue;
      const width = enemy.kind === 'warden' || enemy.kind === 'devourer' ? 54 : 28;
      const x = enemy.x - width / 2;
      const y = enemy.y - enemy.radius - 11;
      this.drawBar(x, y, width, 3, enemy.health / enemy.maxHealth, 0xff5f7f, 0x270812);
      if (enemy.shield > 0)
        this.drawBar(
          x,
          y - 4,
          width,
          2,
          enemy.shield / Math.max(1, enemy.maxHealth * 0.32),
          0xc33cff,
          0x24082e,
        );
    }
    this.renderHuntIndicators(snapshot);
    if (boss) {
      this.drawBar(
        48,
        78,
        WORLD_WIDTH - 96,
        8,
        boss.health / boss.maxHealth,
        0xc33cff,
        0x25092e,
        this.hudGraphics,
      );
    }
  }

  private renderHuntIndicators(snapshot: SimulationSnapshot): void {
    const active =
      snapshot.waveSpawnFinished &&
      snapshot.enemies.length > 0 &&
      snapshot.enemies.length <= 5 &&
      !snapshot.enemies.some((enemy) => enemy.kind === 'warden' || enemy.kind === 'devourer');
    if (!active) return;

    const centerX = WORLD_WIDTH / 2;
    const centerY = WORLD_HEIGHT / 2;
    const halfWidth = WORLD_WIDTH / 2 - 30;
    const halfHeight = WORLD_HEIGHT / 2 - 105;
    const pulse = 0.72 + Math.sin(snapshot.tick * 0.14) * 0.25;
    for (const enemy of snapshot.enemies) {
      this.barsGraphics
        .lineStyle(2, 0xffd321, pulse)
        .strokeCircle(enemy.x, enemy.y, enemy.radius + 10);
      const deltaX = enemy.x - snapshot.player.x;
      const deltaY = enemy.y - snapshot.player.y;
      const screenDeltaX = deltaX * this.dynamicZoom;
      const screenDeltaY = deltaY * this.dynamicZoom;
      if (Math.abs(screenDeltaX) < halfWidth && Math.abs(screenDeltaY) < halfHeight) continue;
      const angle = Math.atan2(screenDeltaY, screenDeltaX);
      const scale = Math.min(
        halfWidth / Math.max(1, Math.abs(Math.cos(angle))),
        halfHeight / Math.max(1, Math.abs(Math.sin(angle))),
      );
      const x = centerX + Math.cos(angle) * scale;
      const y = centerY + Math.sin(angle) * scale;
      const tipX = x + Math.cos(angle) * 10;
      const tipY = y + Math.sin(angle) * 10;
      const leftX = x + Math.cos(angle + 2.45) * 8;
      const leftY = y + Math.sin(angle + 2.45) * 8;
      const rightX = x + Math.cos(angle - 2.45) * 8;
      const rightY = y + Math.sin(angle - 2.45) * 8;
      this.hudGraphics
        .fillStyle(0xffd321, pulse)
        .fillTriangle(tipX, tipY, leftX, leftY, rightX, rightY);
      this.hudGraphics.lineStyle(1, 0xffffff, 0.85).strokeCircle(x, y, 13);
    }
  }

  private drawBar(
    x: number,
    y: number,
    width: number,
    height: number,
    ratio: number,
    fill: number,
    track: number,
    graphics = this.barsGraphics,
  ): void {
    graphics.fillStyle(track, 0.95).fillRoundedRect(x, y, width, height, height / 2);
    graphics
      .fillStyle(fill, 1)
      .fillRoundedRect(x, y, Math.max(0, width * Math.min(1, ratio)), height, height / 2);
  }

  private renderEvents(events: readonly GameEvent[]): void {
    const lowQuality = this.settings?.graphicsQuality === 'low';
    const coreX = this.currentSnapshot?.player.x ?? CORE_X;
    const coreY = this.currentSnapshot?.player.y ?? CORE_Y;
    const quality = this.qualityFactor();
    let deathEffects = 0;
    let impactEffects = 0;
    const deathBudget = Math.round(4 + quality * 12);
    const impactBudget = Math.round(6 + quality * 14);
    for (const event of events) {
      if (event.type === 'teslaFired')
        this.flashPolyline(
          event.payload.points,
          getSkillSkin(this.cosmetics.skillSkinId).tesla,
          this.isEndgameActive() ? 3.2 : 2,
        );
      if (event.type === 'cryoPulse' && !lowQuality && this.settings?.reducedFlashes !== true)
        this.elementalPulse(
          coreX,
          coreY,
          event.payload.radius,
          getSkillSkin(this.cosmetics.skillSkinId).cryo,
          'cryo',
        );
      if (event.type === 'gravityPulse' && !lowQuality && this.settings?.reducedFlashes !== true)
        this.elementalPulse(
          coreX,
          coreY,
          event.payload.radius,
          getSkillSkin(this.cosmetics.skillSkinId).gravity,
          'gravity',
        );
      if (event.type === 'coreAbilityActivated' && this.settings?.reducedFlashes !== true) {
        this.flashRing(coreX, coreY, event.payload.radius, event.payload.color);
        if (!lowQuality) this.particleBurst(coreX, coreY, event.payload.color, 14, 90);
      }
      if (event.type === 'enemyKilled') {
        deathEffects += 1;
        if (deathEffects <= deathBudget)
          this.enemyDeathEffect(
            event.payload.x,
            event.payload.y,
            getEnemy(event.payload.kind).color,
            event.payload.kind,
            lowQuality,
          );
      }
      if (event.type === 'levelUp') {
        const milestone = event.payload.level % 5 === 0;
        const color = milestone ? 0xffd321 : event.payload.level >= 10 ? 0xc33cff : 0x00e5ff;
        this.flashRing(
          coreX,
          coreY,
          milestone ? 250 : 130 + event.payload.level * 4,
          color,
          milestone ? 680 : 420,
        );
        if (!lowQuality)
          this.particleBurst(
            coreX,
            coreY,
            color,
            milestone ? 30 : 10 + Math.min(16, event.payload.level),
            milestone ? 190 : 88 + event.payload.level * 3,
          );
        if (milestone && this.settings?.reducedShake !== true) this.cameras.main.shake(320, 0.006);
      }
      if (event.type === 'systemUpgradeActivated')
        this.systemUpgradeEffect(coreX, coreY, event.payload, lowQuality);
      if (event.type === 'overdriveActivated') {
        this.flashRing(coreX, coreY, 220, 0xffd321);
        if (!lowQuality) this.particleBurst(coreX, coreY, 0xffd321, 28, 180);
        this.screenFlash(0xffd321, 0.2, 300);
        this.combatAnnouncement('OVERDRIVE', 'MAXIMUM FIRE RATE', 0xffd321);
        this.cameraPunch = Math.max(this.cameraPunch, 0.035);
      }
      if (event.type === 'bossEntered') {
        this.flashRing(coreX, coreY, 300, getEnemy(event.payload.kind).color);
        this.screenFlash(getEnemy(event.payload.kind).color, 0.28, 540);
        this.combatAnnouncement('BOSS PROTOCOL', event.payload.name.toUpperCase(), 0xff4f7b);
        this.cameraPunch = Math.max(this.cameraPunch, 0.055);
        if (this.settings?.reducedShake !== true) this.cameras.main.shake(520, 0.01);
      }
      if (event.type === 'bossPhase') {
        const color = event.payload.phase >= 4 ? 0xff265e : 0xc33cff;
        this.screenFlash(color, event.payload.phase >= 4 ? 0.3 : 0.17, 400);
        this.combatAnnouncement(
          `PHASE ${event.payload.phase}`,
          event.payload.phase >= 4 ? 'ANNIHILATION STATE' : 'BOSS PATTERN SHIFT',
          color,
        );
        this.flashRing(coreX, coreY, 260, color, 620);
        this.cameraPunch = Math.max(this.cameraPunch, 0.04);
        if (this.settings?.reducedShake !== true) this.cameras.main.shake(380, 0.008);
      }
      if (event.type === 'bossArenaLocked') {
        this.flashRing(event.payload.x, event.payload.y, event.payload.radius, 0xff265e);
        this.flashRing(
          event.payload.x,
          event.payload.y,
          event.payload.radius * 0.72,
          0xc33cff,
          520,
        );
      }
      if (event.type === 'bossArenaReleased') {
        this.flashRing(coreX, coreY, 260, 0x80ff24, 620);
        if (!lowQuality) this.particleBurst(coreX, coreY, 0x80ff24, 24, 170);
      }
      if (event.type === 'xpCollected') {
        const color = this.xpColor(event.payload.tier);
        const power =
          event.payload.tier === 'prismatic'
            ? 18
            : event.payload.tier === 'gold'
              ? 10
              : event.payload.tier === 'blue'
                ? 5
                : 2;
        if (!lowQuality || event.payload.tier === 'gold' || event.payload.tier === 'prismatic')
          this.particleBurst(event.payload.x, event.payload.y, color, power, 18 + power * 2);
        if (event.payload.tier === 'gold' || event.payload.tier === 'prismatic')
          this.flashRing(event.payload.x, event.payload.y, 34 + power * 2, color, 280);
      }
      if (event.type === 'enemyDamaged' && !lowQuality && this.settings?.damageNumbers !== false) {
        const enemy = this.simulation
          ?.getSnapshot()
          .enemies.find((candidate) => candidate.id === event.payload.enemyId);
        if (enemy) {
          this.damageNumber(enemy.x, enemy.y, event.payload.amount, event.payload.critical);
          if (
            impactEffects < impactBudget &&
            (event.payload.critical || event.payload.enemyId % 3 === 0)
          ) {
            impactEffects += 1;
            this.hitSpark(
              enemy.x,
              enemy.y,
              this.familyColor(event.payload.family),
              event.payload.critical,
            );
          }
        }
      }
      if (event.type === 'coreDamaged' && this.settings?.reducedShake !== true) {
        this.cameras.main.shake(90, 0.004);
      }
      if (event.type === 'evolutionUnlocked' && this.settings?.reducedShake !== true) {
        this.cameras.main.shake(450, 0.009);
      }
    }
    if (deathEffects > 0) this.celebrateComboMilestone();
  }

  private enemyDeathEffect(
    x: number,
    y: number,
    color: number,
    kind: EnemyState['kind'],
    lowQuality: boolean,
  ): void {
    const boss = kind === 'warden' || kind === 'devourer';
    const heavy = boss || kind === 'tank' || kind === 'protector';
    const radius = boss ? 105 : heavy ? 55 : 34;
    this.flashRing(x, y, radius, color, boss ? 620 : 300);
    this.flashRing(x, y, radius * 0.62, 0xffffff, boss ? 460 : 220);
    if (lowQuality) return;
    this.particleBurst(x, y, color, boss ? 42 : heavy ? 14 : 8, boss ? 170 : heavy ? 72 : 46);
    const shardCount = boss ? 16 : heavy ? 8 : 5;
    for (let index = 0; index < shardCount; index += 1) {
      const angle = (Math.PI * 2 * index) / shardCount + Phaser.Math.FloatBetween(-0.2, 0.2);
      const travel = Phaser.Math.FloatBetween(radius * 0.55, radius * 1.25);
      const shard = this.add
        .polygon(0, 0, [0, -4, 3, 4, -3, 4], index % 2 === 0 ? color : 0xeaffff, 0.92)
        .setPosition(x, y)
        .setDepth(27)
        .setRotation(angle);
      this.tweens.add({
        targets: shard,
        x: x + Math.cos(angle) * travel,
        y: y + Math.sin(angle) * travel,
        rotation: angle + Phaser.Math.FloatBetween(-2.4, 2.4),
        scale: 0.15,
        alpha: 0,
        duration: Phaser.Math.Between(280, boss ? 720 : 480),
        ease: 'Quad.Out',
        onComplete: () => shard.destroy(),
      });
    }
  }

  private hitSpark(x: number, y: number, color: number, critical: boolean): void {
    const graphics = this.add.graphics().setPosition(x, y).setDepth(28);
    const rays = critical ? 8 : 4;
    graphics.lineStyle(critical ? 2.5 : 1.5, critical ? 0xffd321 : color, 0.95);
    for (let index = 0; index < rays; index += 1) {
      const angle = (Math.PI * 2 * index) / rays + Phaser.Math.FloatBetween(-0.18, 0.18);
      const inner = critical ? 5 : 3;
      const outer = critical ? 22 : 12;
      graphics.lineBetween(
        Math.cos(angle) * inner,
        Math.sin(angle) * inner,
        Math.cos(angle) * outer,
        Math.sin(angle) * outer,
      );
    }
    this.tweens.add({
      targets: graphics,
      scale: critical ? 1.7 : 1.2,
      alpha: 0,
      duration: critical ? 220 : 130,
      onComplete: () => graphics.destroy(),
    });
  }

  private elementalPulse(
    x: number,
    y: number,
    radius: number,
    color: number,
    type: 'cryo' | 'gravity',
  ): void {
    const graphics = this.add.graphics().setPosition(x, y).setDepth(24);
    graphics.lineStyle(10, color, 0.08).strokeCircle(0, 0, 30);
    graphics.lineStyle(type === 'gravity' ? 3 : 2, color, 0.88).strokeCircle(0, 0, 30);
    const rays = type === 'gravity' ? 14 : 18;
    for (let index = 0; index < rays; index += 1) {
      const angle = (Math.PI * 2 * index) / rays;
      const skew = type === 'gravity' ? 0.22 : 0;
      graphics.lineStyle(index % 3 === 0 ? 2 : 1, index % 2 ? color : 0xeaffff, 0.72);
      graphics.lineBetween(
        Math.cos(angle) * 20,
        Math.sin(angle) * 20,
        Math.cos(angle + skew) * 42,
        Math.sin(angle + skew) * 42,
      );
    }
    this.tweens.add({
      targets: graphics,
      scaleX: radius / 30,
      scaleY: radius / 30,
      rotation: type === 'gravity' ? Math.PI / 2 : 0,
      alpha: 0,
      duration: type === 'gravity' ? 520 : 380,
      ease: 'Cubic.Out',
      onComplete: () => graphics.destroy(),
    });
  }

  private systemUpgradeEffect(
    x: number,
    y: number,
    payload: Extract<GameEvent, { type: 'systemUpgradeActivated' }>['payload'],
    lowQuality: boolean,
  ): void {
    const color =
      payload.rarity === 'legendary'
        ? 0xffd321
        : payload.rarity === 'epic'
          ? 0xc33cff
          : payload.rarity === 'rare'
            ? 0x32b8ff
            : payload.rarity === 'uncommon'
              ? 0x80ff24
              : 0xc8d5dc;
    const mastered = payload.rank >= payload.maxRank;
    const ringCount = mastered ? 4 : payload.rank;
    for (let index = 0; index < ringCount; index += 1) {
      this.time.delayedCall(index * 95, () =>
        this.flashRing(
          x,
          y,
          82 + payload.tier * 18 + index * 28,
          index % 2 === 0 ? color : 0xffffff,
          320 + index * 70,
        ),
      );
    }

    const sides =
      payload.family === 'kinetic'
        ? 3
        : payload.family === 'tesla'
          ? 4
          : payload.family === 'cryo'
            ? 6
            : 8;
    const glyph = this.add.graphics().setPosition(x, y).setDepth(27);
    glyph.lineStyle(mastered ? 4 : 2, color, 0.92);
    for (let layer = 0; layer < Math.max(1, payload.rank); layer += 1) {
      glyph.strokePoints(this.regularPolygon(sides, 34 + layer * 14), true);
    }
    if (payload.family === 'tesla') {
      glyph.lineStyle(2, 0xeaffff, 0.8);
      for (let index = 0; index < 8; index += 1) {
        const angle = (Math.PI * 2 * index) / 8;
        glyph.lineBetween(
          Math.cos(angle) * 22,
          Math.sin(angle) * 22,
          Math.cos(angle + 0.15) * 74,
          Math.sin(angle + 0.15) * 74,
        );
      }
    }
    this.tweens.add({
      targets: glyph,
      rotation: payload.family === 'cryo' ? -Math.PI / 3 : Math.PI / 2,
      scale: mastered ? 2.4 : 1.35 + payload.rank * 0.2,
      alpha: 0,
      duration: mastered ? 760 : 420 + payload.rank * 90,
      ease: mastered ? 'Back.Out' : 'Cubic.Out',
      onComplete: () => glyph.destroy(),
    });
    if (!lowQuality)
      this.particleBurst(
        x,
        y,
        color,
        mastered ? 32 : 8 + payload.rank * 6,
        mastered ? 190 : 82 + payload.rank * 28,
      );
    if (mastered && this.settings?.reducedShake !== true) this.cameras.main.shake(380, 0.008);
  }

  private particleBurst(
    x: number,
    y: number,
    color: number,
    count: number,
    distance: number,
  ): void {
    for (let index = 0; index < count; index += 1) {
      const angle = (Math.PI * 2 * index) / count + Phaser.Math.FloatBetween(-0.16, 0.16);
      const travel = Phaser.Math.FloatBetween(distance * 0.45, distance);
      const particle = this.add
        .circle(x, y, Phaser.Math.FloatBetween(1.5, 3.5), color, 0.9)
        .setDepth(26);
      this.tweens.add({
        targets: particle,
        x: x + Math.cos(angle) * travel,
        y: y + Math.sin(angle) * travel,
        scale: 0.2,
        alpha: 0,
        duration: Phaser.Math.Between(260, 520),
        ease: 'Cubic.Out',
        onComplete: () => particle.destroy(),
      });
    }
  }

  private flashPolyline(
    points: readonly { x: number; y: number }[],
    color: number,
    width: number,
  ): void {
    if (points.length < 2) return;
    const jagged: { x: number; y: number }[] = [points[0]!];
    for (let index = 1; index < points.length; index += 1) {
      const previous = points[index - 1]!;
      const target = points[index]!;
      const deltaX = target.x - previous.x;
      const deltaY = target.y - previous.y;
      const length = Math.hypot(deltaX, deltaY) || 1;
      const normalX = -deltaY / length;
      const normalY = deltaX / length;
      for (let segment = 1; segment <= 3; segment += 1) {
        const ratio = segment / 4;
        const jitter = Phaser.Math.FloatBetween(-7, 7) * Math.min(1.4, length / 60);
        jagged.push({
          x: previous.x + deltaX * ratio + normalX * jitter,
          y: previous.y + deltaY * ratio + normalY * jitter,
        });
      }
      jagged.push(target);
    }
    const graphics = this.add.graphics().setDepth(25).setBlendMode(Phaser.BlendModes.ADD);
    for (const layer of [
      { size: width * 5.5, tint: color, alpha: 0.08 },
      { size: width * 2.5, tint: color, alpha: 0.42 },
      { size: Math.max(1, width * 0.72), tint: 0xeaffff, alpha: 1 },
    ]) {
      graphics.lineStyle(layer.size, layer.tint, layer.alpha).beginPath();
      jagged.forEach((point, index) =>
        index === 0 ? graphics.moveTo(point.x, point.y) : graphics.lineTo(point.x, point.y),
      );
      graphics.strokePath();
    }
    this.tweens.add({
      targets: graphics,
      alpha: 0,
      duration: this.isEndgameActive() ? 230 : 160,
      onComplete: () => graphics.destroy(),
    });
  }

  private flashRing(x: number, y: number, radius: number, color: number, duration = 360): void {
    const graphics = this.add
      .graphics()
      .setPosition(x, y)
      .setDepth(24)
      .setBlendMode(Phaser.BlendModes.ADD);
    graphics.lineStyle(10, color, 0.07).strokeCircle(0, 0, 24);
    graphics.lineStyle(2.2, color, 0.88).strokeCircle(0, 0, 24);
    graphics.lineStyle(1, 0xffffff, 0.55).strokeCircle(0, 0, 20);
    graphics.setScale(1);
    this.tweens.add({
      targets: graphics,
      scaleX: radius / 24,
      scaleY: radius / 24,
      alpha: 0,
      duration,
      ease: 'Cubic.Out',
      onComplete: () => graphics.destroy(),
    });
  }

  private screenFlash(color: number, alpha: number, duration: number): void {
    if (this.settings?.reducedFlashes === true) return;
    const flash = this.add
      .rectangle(WORLD_WIDTH / 2, WORLD_HEIGHT / 2, WORLD_WIDTH, WORLD_HEIGHT, color, alpha)
      .setScrollFactor(0)
      .setDepth(70)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.tweens.add({
      targets: flash,
      alpha: 0,
      duration,
      ease: 'Quad.Out',
      onComplete: () => flash.destroy(),
    });
  }

  private combatAnnouncement(title: string, subtitle: string, color: number): void {
    const container = this.add
      .container(WORLD_WIDTH / 2, WORLD_HEIGHT * 0.32)
      .setDepth(72)
      .setScrollFactor(0);
    const line = this.add.rectangle(0, 0, 280, 2, color, 0.9);
    const heading = this.add
      .text(0, -22, title, {
        fontFamily: 'Oxanium, sans-serif',
        fontSize: '27px',
        fontStyle: 'bold',
        color: Phaser.Display.Color.IntegerToColor(color).rgba,
        stroke: '#05070F',
        strokeThickness: 7,
        align: 'center',
      })
      .setOrigin(0.5);
    const detail = this.add
      .text(0, 12, subtitle, {
        fontFamily: 'Space Grotesk, sans-serif',
        fontSize: '10px',
        fontStyle: 'bold',
        color: '#EAFDFF',
        stroke: '#05070F',
        strokeThickness: 4,
        letterSpacing: 2,
      })
      .setOrigin(0.5);
    container.add([line, heading, detail]).setScale(0.72).setAlpha(0);
    this.tweens.add({
      targets: container,
      scale: 1,
      alpha: 1,
      duration: 190,
      ease: 'Back.Out',
      yoyo: true,
      hold: 420,
      onComplete: () => container.destroy(true),
    });
  }

  private celebrateComboMilestone(): void {
    const combo = this.currentSnapshot?.combo ?? 0;
    const tier = combo >= 100 ? 4 : combo >= 50 ? 3 : combo >= 25 ? 2 : combo >= 10 ? 1 : 0;
    if (tier <= this.lastCelebratedComboTier) return;
    this.lastCelebratedComboTier = tier;
    const labels = [
      ['', ''],
      ['KILL SURGE', `×${combo} CHAIN`],
      ['OVERKILL', `×${combo} CHAIN`],
      ['ANNIHILATION', `×${combo} CHAIN`],
      ['ASCENDANT', `×${combo} CHAIN`],
    ] as const;
    const [title, subtitle] = labels[tier]!;
    const color = tier >= 3 ? 0xffd321 : tier === 2 ? 0xc33cff : 0x00e5ff;
    this.combatAnnouncement(title, subtitle, color);
    this.screenFlash(color, 0.1 + tier * 0.035, 260);
    const x = this.currentSnapshot?.player.x ?? CORE_X;
    const y = this.currentSnapshot?.player.y ?? CORE_Y;
    this.flashRing(x, y, 140 + tier * 38, color, 460);
    this.cameraPunch = Math.max(this.cameraPunch, 0.012 + tier * 0.008);
    if (tier >= 2 && this.settings?.reducedShake !== true)
      this.cameras.main.shake(160 + tier * 45, 0.003 + tier * 0.0012);
  }

  private playEndgameEntrance(snapshot: SimulationSnapshot): void {
    const x = snapshot.player.x;
    const y = snapshot.player.y;
    this.screenFlash(0xc33cff, 0.32, 680);
    this.combatAnnouncement('MAXIMUM OUTPUT', 'ALL SYSTEMS ASCENDANT', 0xffd321);
    for (let ring = 0; ring < 4; ring += 1) {
      this.time.delayedCall(ring * 100, () =>
        this.flashRing(x, y, 150 + ring * 58, ring % 2 === 0 ? 0xffd321 : 0xc33cff, 620),
      );
    }
    if (this.settings?.graphicsQuality !== 'low') {
      this.particleBurst(x, y, 0xffd321, 32, 220);
      this.particleBurst(x, y, 0xc33cff, 28, 180);
    }
    this.cameraPunch = 0.065;
    if (this.settings?.reducedShake !== true) this.cameras.main.shake(520, 0.009);
  }

  private familyColor(family: WeaponFamily): number {
    const skin = getSkillSkin(this.cosmetics.skillSkinId);
    if (family === 'kinetic') return skin.kinetic;
    if (family === 'tesla') return skin.tesla;
    if (family === 'cryo') return skin.cryo;
    return skin.gravity;
  }

  private isEndgameActive(): boolean {
    return this.currentSnapshot ? this.isEndgameSnapshot(this.currentSnapshot) : false;
  }

  private isEndgameSnapshot(snapshot: SimulationSnapshot): boolean {
    return snapshot.wave >= 9 && snapshot.level >= 30 && snapshot.synergies.length >= 5;
  }

  private visualIntensity(snapshot: SimulationSnapshot): number {
    return Phaser.Math.Clamp(
      (snapshot.level - 1) / 28 +
        snapshot.synergies.length * 0.055 +
        (snapshot.evolutionId ? 0.18 : 0),
      0,
      1,
    );
  }

  private qualityFactor(): number {
    if (this.settings?.graphicsQuality === 'low') return 0.45;
    if (this.settings?.graphicsQuality === 'medium') return 0.68;
    if (this.settings?.graphicsQuality === 'ultra') return 1.18;
    return 1;
  }

  private damageNumber(x: number, y: number, amount: number, critical: boolean): void {
    const text = this.add
      .text(x, y, Math.round(amount).toString(), {
        fontFamily: 'Oxanium, sans-serif',
        fontSize: critical ? '17px' : '11px',
        fontStyle: 'bold',
        color: critical ? '#FFD321' : '#E9FBFF',
        stroke: '#05070F',
        strokeThickness: 3,
      })
      .setOrigin(0.5)
      .setDepth(40);
    this.tweens.add({
      targets: text,
      y: y - 24,
      alpha: 0,
      duration: 480,
      onComplete: () => text.destroy(),
    });
  }

  private clearEntityViews(): void {
    this.enemyAuraGraphics?.clear();
    this.enemyDetailGraphics?.clear();
    this.projectileTrailGraphics?.clear();
    this.xpGlowGraphics?.clear();
    this.ascendantCoreSprite?.setVisible(false);
    this.enemyViews.forEach((view) => view.setVisible(false).setActive(false));
    this.projectileViews.forEach((view) => view.setVisible(false).setActive(false));
    this.xpPickupViews.forEach((view) => view.setVisible(false).setActive(false));
    for (const view of this.enemyViews.values()) {
      const kind = String(view.getData('kind'));
      const pool = this.enemyViewPools.get(kind) ?? [];
      pool.push(view);
      this.enemyViewPools.set(kind, pool);
    }
    for (const view of this.projectileViews.values()) {
      const source = view.getData('source') as 'player' | 'enemy';
      const pool = this.projectileViewPools.get(source) ?? [];
      pool.push(view);
      this.projectileViewPools.set(source, pool);
    }
    for (const view of this.xpPickupViews.values()) {
      const tier = view.getData('tier') as XpTier;
      const pool = this.xpPickupViewPools.get(tier) ?? [];
      pool.push(view);
      this.xpPickupViewPools.set(tier, pool);
    }
    this.enemyViews.clear();
    this.projectileViews.clear();
    this.xpPickupViews.clear();
  }

  private regularPolygon(sides: number, radius: number): Phaser.Geom.Point[] {
    return Array.from({ length: sides }, (_, index) => {
      const angle = -Math.PI / 2 + (Math.PI * 2 * index) / sides;
      return new Phaser.Geom.Point(Math.cos(angle) * radius, Math.sin(angle) * radius);
    });
  }

  private hexagon(x: number, y: number, radius: number): Phaser.Geom.Point[] {
    return Array.from({ length: 6 }, (_, index) => {
      const angle = Math.PI / 6 + (Math.PI * 2 * index) / 6;
      return new Phaser.Geom.Point(x + Math.cos(angle) * radius, y + Math.sin(angle) * radius);
    });
  }
}
